"use server";

import { dbg } from "@/lib/debug-log";
import { sendEmail, wrapEmail, getEmailConfig } from "@/lib/email";
import { prisma } from "@/lib/prisma";

// Called by app/error.tsx (a client boundary) whenever it catches a page-level error.
//
// This is the single place where both logging and admin email notification happen
// for unhandled errors. instrumentation.ts onRequestError also fires for these,
// but it can only console.error (no fs/prisma access due to webpack bundle constraints).
// This server action has full access to all server-side modules.

export async function reportClientBoundaryError({
  message,
  stack,
  digest,
  pathname,
}: {
  message: string;
  stack?: string;
  digest?: string;
  pathname: string;
}) {
  // 1. Write to debug log
  dbg.error("error", "Page error boundary caught", {
    message,
    digest,
    pathname,
    stack: stack?.split("\n").slice(0, 8).join("\n"),
  });

  // 2. Email admins — fire-and-forget so a mail failure never surfaces to the user
  notifyAdmin({ message, stack, digest, pathname }).catch((e) =>
    dbg.error("error", "error boundary admin email failed", { error: String(e) })
  );
}

async function notifyAdmin({
  message,
  stack,
  digest,
  pathname,
}: {
  message: string;
  stack?: string;
  digest?: string;
  pathname: string;
}) {
  const emailCfg = await getEmailConfig();
  if (!emailCfg) return;

  const admins = await prisma.user.findMany({
    where: { role: "ADMIN", emailVerified: { not: null } },
    select: { email: true },
  });
  if (admins.length === 0) return;

  const siteNameRow = await prisma.siteConfig.findUnique({ where: { key: "site_name" } });
  const siteName = siteNameRow?.value ?? "Baby Name Championship";

  const when = new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  const stackLines = stack
    ? stack.split("\n").slice(0, 10).map((l) => l.trim()).join("\n")
    : "No stack available";

  const html = wrapEmail(`
    <h2 style="color:#b91c1c;font-size:20px;margin:0 0 8px;">🚨 Page Error Caught</h2>
    <p style="color:#6b7280;font-size:13px;margin:0 0 20px;">${esc(when)}</p>

    <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:12px;padding:16px 20px;margin-bottom:20px;">
      <div style="font-weight:900;color:#991b1b;font-size:15px;margin-bottom:6px;">${esc(message)}</div>
      ${digest ? `<div style="font-size:12px;color:#9ca3af;margin-bottom:6px;">Digest: ${esc(digest)}</div>` : ""}
      <div style="font-size:12px;color:#6b7280;">Page: ${esc(pathname)}</div>
    </div>

    <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;padding:16px 20px;margin-bottom:20px;">
      <div style="font-size:11px;font-weight:900;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px;">Stack Trace</div>
      <pre style="font-size:11px;color:#374151;margin:0;white-space:pre-wrap;word-break:break-all;font-family:monospace;">${esc(stackLines)}</pre>
    </div>

    <p style="color:#9ca3af;font-size:12px;margin:0;">
      The user saw the error page and can retry. Check <strong>Profile → Debug Log</strong> for full context.
    </p>
  `, siteName);

  const text = `Page error on ${siteName}\n\nPage: ${pathname}\n${message}\n\nStack:\n${stackLines}`;

  await sendEmail({
    to: admins.map((a) => a.email),
    subject: `🚨 ${siteName} — Page Error: ${message.slice(0, 80)}`,
    text,
    html,
  });
}

function esc(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
